import { useEffect, useRef } from "react";
import {
  PAINTS,
  SEASON_MODULE_LEVEL,
  paintIdSchema,
  type PaintId,
  type SeasonView,
} from "@frontier/content/season";
import {
  SeasonRewardArt,
  SeasonRewardCard,
  seasonRewardName,
} from "./SeasonRewardCard";
import { credits } from "./ui-labels";
import "./station-pages.css";
import "./commerce.css";
import "./season.css";
import "./season-recovery.css";

export function SeasonPanel({
  view,
  busy,
  pendingUnlock = false,
  onReload,
  onClaim,
  onUnlock,
  onPaint,
  onShop,
}: {
  view: SeasonView | null;
  busy: boolean;
  pendingUnlock?: boolean;
  onReload: () => Promise<void>;
  onClaim: (level: number) => void;
  onUnlock: () => void;
  onPaint: (paintId: PaintId) => void;
  onShop: () => void;
}) {
  const rail = useRef<HTMLDivElement>(null);
  const premiumOffer = useRef<HTMLElement>(null);
  const initialSeason = useRef("");
  const recovery = pendingUnlock && <div className="season-unlock-recovery" role="status">
    <span>Покупка доступа не подтверждена.</span>
    <button className="station-button station-button-gold" disabled={busy} onClick={onUnlock}>Проверить покупку</button>
  </div>;
  const focusLevel = (level: number, reveal = false) => {
    const viewport = rail.current;
    const target = viewport?.querySelector<HTMLElement>(
      `[data-season-level="${level}"]`,
    );
    if (viewport && target) {
      viewport.scrollTo({ left: target.offsetLeft, behavior: "auto" });
      if (reveal) {
        target.focus({ preventScroll: true });
        viewport
          .closest(".season-journey")
          ?.scrollIntoView({ block: "start", inline: "nearest" });
      }
    }
  };
  useEffect(() => {
    void onReload();
    const timer = window.setInterval(() => void onReload(), 10000);
    return () => window.clearInterval(timer);
  }, [onReload]);
  useEffect(() => {
    if (!view) {
      initialSeason.current = "";
      return;
    }
    if (initialSeason.current === view.id) return;
    initialSeason.current = view.id;
    focusLevel(Math.max(1, view.level));
  }, [view]);
  if (!view)
    return (
      <div className="station-page commerce-loading">
        <span className="station-page-kicker">СЕЗОННЫЙ ПРОПУСК</span>
        <h2>Загрузка сезона…</h2>
        {recovery}
        <button className="station-button" onClick={() => void onReload()}>
          Обновить
        </button>
      </div>
    );
  const ended = view.serverNow >= view.endsAt;
  const tasks = view.tasks ?? [];
  const readyPremium = view.rewards.filter(
    (reward) =>
      reward.premiumOnly && reward.level <= view.level && !reward.claimed,
  ).length;
  const available = view.rewards.filter(
    (reward) =>
      !reward.claimed &&
      reward.level <= view.level &&
      (!reward.premiumOnly || view.premium),
  );
  const milestones = view.rewards
    .filter((reward) => reward.equipment || reward.paint)
    .toSorted((a, b) => a.level - b.level);
  const featured =
    milestones.find(
      (reward) =>
        !reward.claimed &&
        reward.level <= view.level &&
        (!reward.premiumOnly || view.premium),
    ) ??
    milestones.find((reward) => reward.level > view.level) ??
    milestones.at(-1);
  const maximum = view.level >= view.levels;
  const levelXp = maximum ? view.xpPerLevel : view.xp % view.xpPerLevel;
  const endsOn = new Date(view.endsAt).toLocaleDateString("ru-RU");
  const moveRail = (direction: number) => {
    const viewport = rail.current;
    if (viewport)
      viewport.scrollBy({
        left: direction * viewport.clientWidth * 0.85,
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
      });
  };
  return (
    <section
      className={`station-page season-panel${pendingUnlock ? " season-panel-pending" : ""}`}
      aria-label="Сезонный пропуск"
    >
      <header className="season-hero">
        <div className="season-hero-copy">
          <div className="season-hero-eyebrow">
            <span className="station-page-kicker">СЕЗОННЫЙ ПРОПУСК</span>
            <span className="season-date">
              {ended ? "ЗАВЕРШЁН" : `ДО ${endsOn}`}
            </span>
          </div>
          <h2>{view.name}</h2>
          {ended && <p>Сезон завершён. Заработанные награды доступны.</p>}
          <div className="season-progress">
            <div className="season-level-value">
              <b>{view.level.toString().padStart(2, "0")}</b>
              <span>
                УРОВЕНЬ
                <br />
                <strong>/ {view.levels}</strong>
              </span>
            </div>
            <div className="season-progress-meter">
              <div>
                <span>
                  {maximum
                    ? "Все уровни открыты"
                    : ended
                      ? `На момент завершения: ${levelXp}/${view.xpPerLevel} XP`
                      : `До уровня ${view.level + 1}: ${view.xpPerLevel - levelXp} XP`}
                </span>
                <b>{view.xp.toLocaleString("ru-RU")} XP</b>
              </div>
              <progress
                aria-label="Опыт текущего уровня"
                max={view.xpPerLevel}
                value={levelXp}
              />
              <small>Добыча · противники · контракты · события</small>
            </div>
          </div>
        </div>
        {featured && (
          <button
            className="season-featured"
            onClick={() => focusLevel(featured.level, true)}
            aria-label={`Показать награду уровня ${featured.level}: ${seasonRewardName(featured)}`}
          >
            <span className="season-featured-art">
              <SeasonRewardArt reward={featured} />
            </span>
            <small>
              {!featured.premiumOnly ? "БЕСПЛАТНО" : "ПРЕМИУМ"} · УРОВЕНЬ{" "}
              {featured.level}
            </small>
            <b>{seasonRewardName(featured)}</b>
            <span>Смотреть награду ↗</span>
          </button>
        )}
      </header>
      {recovery}
      <div className="season-utilities">
        {tasks.length > 0 && (
          <details className="season-tasks">
            <summary>
              Недельные задачи · {tasks.filter((task) => task.completed).length}
              /{tasks.length}
              <span aria-hidden="true">⌄</span>
            </summary>
            <p>
              Новые задачи — каждую неделю. Открытые выполняются одновременно и
              остаются до конца сезона. Опыт начисляется автоматически.
            </p>
            <div>
              {tasks.map((task) => (
                <article
                  key={task.id}
                  className={task.completed ? "season-task-complete" : ""}
                >
                  <div>
                    <small>НЕДЕЛЯ {task.week}</small>
                    <b>+{task.xp} XP</b>
                  </div>
                  <strong>{task.title}</strong>
                  <progress
                    value={task.progress}
                    max={task.target}
                    aria-label={task.title}
                  />
                  <span>
                    {task.completed
                      ? "Выполнено · опыт начислен"
                      : ended
                        ? `Сезон завершён · ${task.progress}/${task.target}`
                        : `${task.progress}/${task.target}`}
                  </span>
                </article>
              ))}
            </div>
          </details>
        )}
        <label className="paint-picker">
          <span>Окраска корпуса</span>
          <select
            aria-label="Окраска корпуса"
            value={view.selectedPaint}
            disabled={busy}
            onChange={(event) =>
              onPaint(paintIdSchema.parse(event.target.value))
            }
          >
            {view.paints.map((id) => (
              <option key={id} value={id}>
                {PAINTS[id].name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <section className="season-journey" aria-label="Награды за уровни">
        <div className="season-journey-heading">
          <div>
            <span className="station-page-kicker">
              {view.levels} УРОВНЕЙ · НАГРАДЫ ЗА ПРОГРЕСС
            </span>
            <h3>Награды сезона</h3>
          </div>
          <div className="season-journey-controls">
            {available[0] && (
              <button
                className="station-button season-ready-link"
                onClick={() => focusLevel(available[0]?.level ?? 1, true)}
              >
                К наградам · {available.length}
              </button>
            )}
            <button
              className="season-rail-arrow"
              onClick={() => moveRail(-1)}
              aria-label="Предыдущие уровни"
            >
              ←
            </button>
            <button
              className="season-rail-arrow"
              onClick={() => moveRail(1)}
              aria-label="Следующие уровни"
            >
              →
            </button>
          </div>
        </div>
        <div
          className="season-rewards"
          ref={rail}
          tabIndex={0}
          role="region"
          aria-label="Лента уровней сезонного пропуска"
        >
          {Array.from({ length: view.levels }, (_, index) => (
            <article
              className={`season-level ${index + 1 === view.level ? "season-level-current" : ""}`}
              key={index}
              data-season-level={index + 1}
              tabIndex={-1}
              aria-label={`Уровень ${index + 1}`}
            >
              <div className="season-level-heading">
                <span>УРОВЕНЬ</span>
                <b>{(index + 1).toString().padStart(2, "0")}</b>
                {index + 1 === view.level && <small>ТВОЙ УРОВЕНЬ</small>}
              </div>
              {view.rewards
                .filter((reward) => reward.level === index + 1)
                .map((reward) => (
                  <SeasonRewardCard
                    key={reward.level}
                    reward={reward}
                    level={view.level}
                    premium={view.premium}
                    busy={busy}
                    onClaim={onClaim}
                    onPremium={() => {
                      premiumOffer.current?.focus({ preventScroll: true });
                      premiumOffer.current?.scrollIntoView({
                        block: "nearest",
                      });
                    }}
                  />
                ))}
            </article>
          ))}
        </div>
      </section>
      <aside
        ref={premiumOffer}
        tabIndex={-1}
        className={`season-access ${view.premium ? "season-access-open" : ""}`}
        aria-label="Премиум-доступ"
      >
        <span className="station-page-kicker">ПРЕМИУМ-ДОСТУП</span>
        <h3>{view.premium ? "Доступ открыт" : "Все награды сезона"}</h3>
        {!view.premium && <p>{readyPremium} премиум-наград уже заработано</p>}
        {!view.premium && (
          <>
            {view.unlockPrice ? (
              <>
                <p>
                  За {view.levels} уровней:{" "}
                  {credits(view.unlockPrice.paidCredits)} КР и{" "}
                  {view.unlockPrice.paidComponents.toLocaleString("ru-RU")}{" "}
                  комп. премиум-наград.
                </p>
                <div className="season-access-price">
                  <b>
                    {credits(view.unlockPrice.credits)} <small>КР</small>
                  </b>
                  <span>
                    + {view.unlockPrice.components.toLocaleString("ru-RU")}{" "}
                    комп.
                  </span>
                </div>
              </>
            ) : (
              <p className="season-price-unavailable">
                Цена временно недоступна
              </p>
            )}
            <button
              className="station-button station-button-gold"
              disabled={busy || pendingUnlock || ended || !view.unlockPrice}
              onClick={onUnlock}
            >
              Открыть премиум
            </button>
            <button className="season-shop-link" onClick={onShop}>
              Пополнить ресурсы +
            </button>
          </>
        )}
        {view.premium && (
          <span className="season-access-check" aria-hidden="true">
            ✓
          </span>
        )}
      </aside>
      <div className="season-conditions">
        <p>
          Реактор — бесплатно на {SEASON_MODULE_LEVEL}-м уровне. Чертёж для всех
          — с 22.12.2026.
        </p>
      </div>
    </section>
  );
}
